/**
 * Visitor identity layer — opaque ID that survives across page loads
 * so the agent can recognise returning visitors (R1).
 *
 * Storage layers, in priority order:
 *   1. `__sp_visitor` cookie (1yr TTL, SameSite=Lax). Set on the host
 *      page domain. Survives full reloads + cross-page nav.
 *   2. `localStorage.__sp_visitor`. Fallback for hosts with cookies
 *      disabled at the document level (rare). Same string format.
 *   3. Browser-hash fingerprint. Derived from UA + language +
 *      screen.{width,height} + timezone. Stable across page loads
 *      WITHOUT any storage, so it catches cookie-cleared / incognito
 *      returners. Lower-fidelity (multiple visitors on a shared
 *      device collide) but better than treating every page load as
 *      a brand-new visitor.
 *
 * DNT respect: when `navigator.doNotTrack === '1'` we DO NOT set the
 * cookie OR localStorage entry. We still derive the browser hash —
 * that's not personally-identifying storage, it's a per-session
 * grouping signal. Visitor coverage drops on DNT pages, by design.
 *
 * The fingerprint string is opaque from the server's perspective: any
 * stable string is fine. Format here is `c_<uuid>` for cookie-derived
 * IDs and `h_<hex>` for hash-derived fallbacks, so server logs can
 * tell the source apart for debugging.
 */

const COOKIE_NAME = '__sp_visitor';
const LS_KEY = '__sp_visitor';
const COOKIE_TTL_DAYS = 365;

let cached: string | null = null;

export function getOrCreateVisitorFingerprint(): string {
  if (cached) return cached;
  if (typeof window === 'undefined') return 'h_ssr';

  const respectDnt = isDoNotTrack();

  // 1. Cookie — primary, when DNT not asserted.
  if (!respectDnt) {
    const fromCookie = readCookie(COOKIE_NAME);
    if (fromCookie) {
      cached = fromCookie;
      return fromCookie;
    }
  }

  // 2. localStorage — fallback for blocked cookies (rare).
  if (!respectDnt) {
    try {
      const ls = window.localStorage?.getItem(LS_KEY);
      if (ls) {
        cached = ls;
        // Best-effort cookie write so the next page load goes through
        // path 1 instead of localStorage.
        writeCookie(COOKIE_NAME, ls, COOKIE_TTL_DAYS);
        return ls;
      }
    } catch {
      /* localStorage blocked — fall through */
    }
  }

  // 3. Fresh ID. Stamp both cookie + localStorage (when allowed) so
  // either path works on subsequent loads.
  if (!respectDnt) {
    const fresh = `c_${cryptoUuid()}`;
    try {
      writeCookie(COOKIE_NAME, fresh, COOKIE_TTL_DAYS);
      window.localStorage?.setItem(LS_KEY, fresh);
    } catch {
      /* storage blocked — fall through to hash */
    }
    if (readCookie(COOKIE_NAME) === fresh || hasLocalStorageEntry()) {
      cached = fresh;
      return fresh;
    }
  }

  // 4. Browser-hash fallback. Storage-free, lower fidelity. Used when
  // DNT is asserted OR when cookies + localStorage both blocked.
  const hash = browserHash();
  cached = `h_${hash}`;
  return cached;
}

/**
 * Reset the cached ID. For tests + a future "Forget me" GDPR flow.
 * Doesn't clear cookie/localStorage — caller does that.
 */
export function __resetVisitorIdCacheForTests(): void {
  cached = null;
}

// ─── Internals ────────────────────────────────────────────────────────

function isDoNotTrack(): boolean {
  if (typeof navigator === 'undefined') return false;
  // Spec-compliant flag — '1' means opt-out, '0' means opt-in, null
  // means unspecified. We respect 1 only; unspecified treated as
  // consent-by-default (matches how nearly every analytics SDK works,
  // including PostHog. Customers can layer a consent banner that
  // sets navigator.doNotTrack via a polyfill if needed.)
  return (
    navigator.doNotTrack === '1' ||
    (navigator as unknown as { msDoNotTrack?: string }).msDoNotTrack === '1' ||
    (window as unknown as { doNotTrack?: string }).doNotTrack === '1'
  );
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      return decodeURIComponent(trimmed.slice(prefix.length));
    }
  }
  return null;
}

function writeCookie(name: string, value: string, days: number): void {
  if (typeof document === 'undefined') return;
  const maxAge = days * 24 * 60 * 60;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  // Scope to the registered apex so `acme.com` and `www.acme.com` (and
  // any other subdomain a customer might run the widget on) share ONE
  // fingerprint cookie. Without this, every subdomain mints its own
  // fresh fingerprint and one visitor becomes 2-3+ DB rows — HubSpot
  // enrichment lands on row A while the chat session attaches to row
  // B, leaving the inbox forever "Anonymous." Skip Domain for IPs +
  // localhost (browsers reject cross-subdomain cookies on those).
  const domain = effectiveCookieDomain();
  const domainAttr = domain ? `; Domain=${domain}` : '';
  document.cookie =
    `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/;` +
    ` SameSite=Lax${secure}${domainAttr}`;
}

/**
 * The Domain= attribute to use when writing fingerprint cookies. We
 * use a conservative "strip leading www." heuristic — that captures
 * the vast majority of customer setups (acme.com vs www.acme.com)
 * without needing the Public Suffix List. Multi-level subdomains
 * (a.b.acme.com) still consolidate at b.acme.com which is correct
 * for those customers. Returns null for IPs / localhost / single-label
 * hosts where a Domain attribute would be invalid.
 */
function effectiveCookieDomain(): string | null {
  if (typeof window === 'undefined') return null;
  const host = window.location.hostname;
  if (!host) return null;
  // IPv4 / IPv6 → no Domain.
  if (/^[\d.]+$/.test(host) || host.includes(':')) return null;
  // localhost variants → no Domain.
  if (host === 'localhost' || host.endsWith('.localhost')) return null;
  // Single-label hostnames (intranet, custom hosts) → no Domain.
  if (!host.includes('.')) return null;
  return host.replace(/^www\./, '');
}

function hasLocalStorageEntry(): boolean {
  try {
    return !!window.localStorage?.getItem(LS_KEY);
  } catch {
    return false;
  }
}

function cryptoUuid(): string {
  // crypto.randomUUID is widely available in the browsers we support
  // (Chrome 92+, FF 95+, Safari 15.4+). Fallback to a manual UUIDv4 for
  // older surfaces. Both produce a 36-char canonical UUID string.
  const cry = (
    typeof crypto !== 'undefined' ? crypto : ({} as Crypto)
  ) as Crypto & { randomUUID?: () => string };
  if (typeof cry.randomUUID === 'function') return cry.randomUUID();
  const bytes = new Uint8Array(16);
  cry.getRandomValues(bytes);
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return (
    `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-` +
    `${hex.slice(16, 20)}-${hex.slice(20)}`
  );
}

/**
 * Cheap, stable string identifying this browser/device combination.
 * Inputs are all storage-free + non-PII per individual axis:
 *   - userAgent     (publicly readable header)
 *   - language      (browser preference)
 *   - screen w/h    (device resolution)
 *   - timezone      (IANA name)
 *
 * SHA-256 of the joined inputs, truncated to 32 hex chars (128 bits)
 * — plenty of entropy to dedupe within a customer site. Same browser
 * on the same device returns the same hash without storage.
 *
 * NOT a privacy-bypass — visitors actively opting out (DNT, clearing
 * cookies) still produce a hash, and that's intended: it's a per-
 * session grouping signal, not durable cross-site tracking.
 */
function browserHash(): string {
  const parts: string[] = [];
  if (typeof navigator !== 'undefined') {
    parts.push(navigator.userAgent || '');
    parts.push(navigator.language || '');
  }
  if (typeof window !== 'undefined' && window.screen) {
    parts.push(String(window.screen.width));
    parts.push(String(window.screen.height));
  }
  try {
    parts.push(Intl.DateTimeFormat().resolvedOptions().timeZone || '');
  } catch {
    /* old browser — skip */
  }
  return djb2(parts.join('|'));
}

/**
 * djb2 hash → hex, 32 chars. We use this instead of crypto.subtle
 * because subtle.digest is async, and we want a synchronous getter
 * (so the visitor ID is available the moment the widget boots, before
 * the first async tick). djb2 collision rate at our scale (max ~1M
 * visitors / customer / month) is acceptable for a fingerprint
 * fallback that's already lower-fidelity than the cookie path.
 */
function djb2(str: string): string {
  // Two 32-bit accumulators combined for 64-bit-equivalent output —
  // single 32-bit hash collides at ~65k entries (birthday paradox).
  let h1 = 5381;
  let h2 = 52711;
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    h1 = ((h1 * 33) ^ c) >>> 0;
    h2 = ((h2 * 31) ^ c) >>> 0;
  }
  return h1.toString(16).padStart(8, '0') + h2.toString(16).padStart(8, '0');
}
